import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { getImageUrl } from "../utils/getImageUrl";
import {
  X, Phone, MapPin, Star, Package, DollarSign,
  Navigation, Award, AlertCircle, RefreshCw,
  Truck, Clock,
} from "lucide-react";
import API from "../api/axiosConfig";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

dayjs.extend(relativeTime);
dayjs.locale("es");

const VEHICLE_EMOJI = { moto: "🛵", bicicleta: "🚲", carro: "🚗", camion: "🚛", otro: "📦" };
const VEHICLE_LABEL = { moto: "Moto", bicicleta: "Bicicleta", carro: "Carro", camion: "Camión", otro: "Otro" };

const STATUS_META = {
  disponible: { label: "Disponible", bg: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", ring: "ring-emerald-400/60" },
  ocupado:    { label: "Ocupado",    bg: "bg-amber-100 text-amber-800",     dot: "bg-amber-500",   ring: "ring-amber-400/60"   },
  offline:    { label: "Offline",    bg: "bg-gray-100 text-gray-500",       dot: "bg-gray-400",    ring: "ring-gray-300/60"    },
};

function buildRyderIcon(vehicleType) {
  const emoji = VEHICLE_EMOJI[vehicleType] ?? "🛵";
  return L.divIcon({
    html: `<div style="font-size:30px;line-height:1;filter:drop-shadow(0 2px 6px rgba(0,0,0,0.45))">${emoji}</div>`,
    className: "",
    iconSize: [38, 38],
    iconAnchor: [19, 38],
  });
}

function MapAutoCenter({ lat, lng }) {
  const map = useMap();
  const prevRef = useRef({ lat: null, lng: null });
  useEffect(() => {
    if (!lat || !lng) return;
    const prev = prevRef.current;
    if (prev.lat === lat && prev.lng === lng) return;
    prevRef.current = { lat, lng };
    map.setView([lat, lng], map.getZoom(), { animate: true, duration: 0.7 });
  }, [lat, lng, map]);
  return null;
}

function StatPill({ icon: Icon, label, value, accent }) {
  return (
    <div className={`flex flex-col items-center justify-center rounded-2xl px-3 py-3 ${accent}`}>
      <Icon size={15} className="mb-1 opacity-70" />
      <p className="text-base font-black leading-none">{value}</p>
      <p className="text-[10px] mt-0.5 opacity-70 leading-tight text-center">{label}</p>
    </div>
  );
}

export default function RyderProfileModal({ ryder: initial, onClose, orders = [] }) {
  const [ryder, setRyder]       = useState(initial);
  const [stats, setStats]       = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastLocUpdate, setLastLocUpdate] = useState(null);
  const intervalRef = useRef(null);

  // Find this ryder's current active order from the dispatch queue
  const activeOrder = orders.find(
    o => o.ryder?.id === initial.id && ["asignado", "en_camino"].includes(o.status)
  );

  // Fetch stats once on mount
  useEffect(() => {
    API.get(`/ryders/${initial.id}/stats`)
      .then(r => setStats(r.data?.data ?? r.data))
      .catch(() => {});
  }, [initial.id]);

  // Refresh location from server
  const refreshLocation = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const r = await API.get(`/ryders/${initial.id}`);
      const d = r.data?.data?.ryder ?? r.data?.ryder;
      if (d) {
        setRyder(prev => ({
          ...prev,
          lat: d.lat,
          lng: d.lng,
          status: d.status,
          locationUpdatedAt: d.locationUpdatedAt,
        }));
        setLastLocUpdate(new Date());
      }
    } catch {}
    finally { if (showSpinner) setRefreshing(false); }
  };

  // Poll every 10 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => refreshLocation(false), 10000);
    return () => clearInterval(intervalRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const meta     = STATUS_META[ryder.status] ?? STATUS_META.offline;
  const name     = ryder.user?.name || "Sin nombre";
  const phone    = ryder.user?.phone;
  const initials = name.slice(0, 2).toUpperCase();
  const lat      = ryder.lat  ? Number(ryder.lat)  : null;
  const lng      = ryder.lng  ? Number(ryder.lng)  : null;
  const hasLoc   = lat && lng && lat !== 0 && lng !== 0;

  const deliveriesToday = initial.deliveriesToday ?? stats?.deliveries?.today ?? 0;
  const earningsToday   = initial.earningsToday   ?? stats?.earnings?.today   ?? 0;
  const totalDeliveries = ryder.totalDeliveries   ?? stats?.deliveries?.total ?? 0;
  const rating          = Number(ryder.rating || 0).toFixed(1);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
        style={{ background: "rgba(15,15,25,0.65)", backdropFilter: "blur(6px)" }}
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.97 }}
          animate={{ opacity: 1, y: 0,  scale: 1    }}
          exit={{   opacity: 0, y: 20,  scale: 0.97 }}
          transition={{ type: "spring", damping: 28, stiffness: 320 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col"
          style={{ maxHeight: "92vh" }}
        >

          {/* ── Header gradient ── */}
          <div className="relative bg-gradient-to-br from-slate-800 via-indigo-900 to-violet-900 px-5 pt-5 pb-6">

            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition"
            >
              <X size={16} />
            </button>

            {/* Refresh location */}
            <button
              onClick={() => refreshLocation(true)}
              disabled={refreshing}
              className="absolute top-4 right-14 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 transition disabled:opacity-40"
              title="Actualizar ubicación"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
            </button>

            {/* Avatar + info */}
            <div className="flex items-center gap-4">
              <div className={`relative w-16 h-16 rounded-2xl flex items-center justify-center text-lg font-black text-white shrink-0 ring-4 ${meta.ring}`}
                style={{ background: "rgba(255,255,255,0.12)" }}>
                {ryder.avatar
                  ? <img src={getImageUrl(ryder.avatar)} alt={name} className="w-full h-full object-cover rounded-2xl" />
                  : <span className="text-xl">{initials}</span>
                }
                <span className={`absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-full border-[2.5px] border-white ${meta.dot}`} />
              </div>

              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-black text-white leading-tight truncate tracking-tight">{name}</h2>
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${meta.bg}`}>
                    {meta.label}
                  </span>
                  {ryder.vehicleType && (
                    <span className="text-[11px] text-white/65 flex items-center gap-1">
                      {VEHICLE_EMOJI[ryder.vehicleType]} {VEHICLE_LABEL[ryder.vehicleType] ?? ryder.vehicleType}
                      {ryder.vehiclePlate && <span className="text-white/40">· {ryder.vehiclePlate}</span>}
                    </span>
                  )}
                </div>
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-white/55 hover:text-white transition"
                  >
                    <Phone size={10} /> {phone}
                  </a>
                )}
              </div>
            </div>

            {/* Stats strip */}
            <div className="mt-4 grid grid-cols-4 gap-2">
              <StatPill icon={Package}    label="Entregas hoy"    value={deliveriesToday}                               accent="bg-white/10 text-white" />
              <StatPill icon={DollarSign} label="Ganancias hoy"   value={`$${earningsToday.toLocaleString()}`}          accent="bg-emerald-500/20 text-emerald-200" />
              <StatPill icon={Star}       label="Calificación"    value={`${rating}★`}                                  accent="bg-amber-500/20 text-amber-200" />
              <StatPill icon={Award}      label="Total"           value={totalDeliveries}                               accent="bg-white/10 text-white" />
            </div>
          </div>

          {/* ── Scrollable body ── */}
          <div className="flex-1 overflow-y-auto">

            {/* Active order banner */}
            {activeOrder && (
              <div className="mx-4 mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <Navigation size={13} className="text-amber-600 shrink-0" />
                  <span className="text-xs font-bold text-amber-800 flex-1">Pedido activo</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full
                    ${activeOrder.status === "en_camino"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-violet-100 text-violet-700"}`}>
                    {activeOrder.status === "en_camino" ? "En camino" : "Asignado"}
                  </span>
                </div>
                <p className="text-sm font-bold text-gray-900">
                  #{activeOrder.orderNumber ?? activeOrder.id?.slice(-6).toUpperCase()}
                </p>
                {activeOrder.deliveryAddress && (
                  <p className="text-xs text-gray-600 mt-0.5 flex items-start gap-1">
                    <MapPin size={11} className="mt-0.5 shrink-0" />
                    {activeOrder.deliveryAddress}
                  </p>
                )}
                <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500">
                  {activeOrder.user?.name && (
                    <span className="flex items-center gap-1"><Truck size={10} />{activeOrder.user.name}</span>
                  )}
                  {activeOrder.createdAt && (
                    <span className="flex items-center gap-1"><Clock size={10} />{dayjs(activeOrder.createdAt).fromNow()}</span>
                  )}
                </div>
              </div>
            )}

            {/* Live Map */}
            <div className="mx-4 mt-4 mb-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                  <MapPin size={13} className="text-indigo-500" />
                  Ubicación en tiempo real
                </p>
                <p className="text-[10px] text-gray-400">
                  {lastLocUpdate
                    ? `Actualizado ${dayjs(lastLocUpdate).fromNow()}`
                    : ryder.locationUpdatedAt
                      ? `Última GPS ${dayjs(ryder.locationUpdatedAt).fromNow()}`
                      : "Sin datos"}
                </p>
              </div>

              {hasLoc ? (
                <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: 250 }}>
                  <MapContainer
                    center={[lat, lng]}
                    zoom={16}
                    style={{ height: "100%", width: "100%" }}
                    zoomControl={true}
                    attributionControl={false}
                    scrollWheelZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      position={[lat, lng]}
                      icon={buildRyderIcon(ryder.vehicleType)}
                    />
                    <Circle
                      center={[lat, lng]}
                      radius={100}
                      pathOptions={{
                        color: "#6366f1",
                        fillColor: "#6366f1",
                        fillOpacity: 0.12,
                        weight: 1.5,
                        dashArray: "4 4",
                      }}
                    />
                    <MapAutoCenter lat={lat} lng={lng} />
                  </MapContainer>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center py-10 text-center">
                  <AlertCircle size={28} className="text-gray-300 mb-2" />
                  <p className="text-sm font-semibold text-gray-500">Ubicación no disponible</p>
                  <p className="text-xs text-gray-400 mt-0.5">El domiciliario no ha compartido su posición GPS</p>
                </div>
              )}
            </div>

            {/* Vehicle & vehicle details */}
            {(ryder.vehicleBrand || ryder.vehicleModel || ryder.vehicleCapacity) && (
              <div className="mx-4 mb-4 mt-2 bg-gray-50 rounded-2xl px-4 py-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Vehículo</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  {ryder.vehicleBrand && <span><span className="text-gray-400">Marca:</span> {ryder.vehicleBrand}</span>}
                  {ryder.vehicleModel && <span><span className="text-gray-400">Modelo:</span> {ryder.vehicleModel}</span>}
                  {ryder.vehicleCapacity && <span><span className="text-gray-400">Capacidad:</span> {ryder.vehicleCapacity}</span>}
                </div>
              </div>
            )}

            {/* Weekly / monthly stats from /ryders/:id/stats */}
            {stats && (
              <div className="mx-4 mb-5">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Rendimiento</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Esta semana", deliveries: stats.deliveries?.week ?? 0, earnings: stats.earnings?.week ?? 0 },
                    { label: "Este mes",    deliveries: stats.deliveries?.month ?? 0, earnings: stats.earnings?.month ?? 0 },
                    { label: "Total",       deliveries: stats.deliveries?.total ?? 0, earnings: stats.earnings?.total ?? 0 },
                  ].map(({ label, deliveries, earnings }) => (
                    <div key={label} className="bg-gray-50 rounded-2xl p-3 text-center border border-gray-100">
                      <p className="text-[10px] text-gray-400 font-medium mb-1">{label}</p>
                      <p className="text-sm font-black text-gray-900">{deliveries}</p>
                      <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">${earnings.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
